import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { CustomerProfile } from './customer-profile.js';

export class CustomerUpdatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = '3ba4f21ad5c94e9dbcead49408fb1898';

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly customer: CustomerProfile,
  ) {
    super();
  }

  get $typeId(): string {
    return CustomerUpdatedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): CustomerUpdatedEvent {
    return new CustomerUpdatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource), SyncEventSource),
        context.fromData<CustomerProfile>(MapUtils.get<SerializedData>(map, 'customer')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'customerId': this.customer.uniqueId,
      'customer': context.toData(this.customer),
    };
  }

  clone(): this {
    return new CustomerUpdatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.customer.clone(),
    ) as this;
  }

  isSame(other: CustomerUpdatedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        isSameData(other.customer, this.customer);
  }
}

export class CustomerUpdatedEventBuilder extends DataModelBuilder<CustomerUpdatedEvent> {
  build(context: SerializationContext, map: SerializedData): CustomerUpdatedEvent {
    return CustomerUpdatedEvent.fromMap(context, map);
  }
}
